import { getSearchParams } from "../../../utils/request";
import { api } from "../../api";
import { patchChatCaches } from "./cacheUtils";

const chatApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getChatConversations: builder.query({
      query: (params = {}) => `chat/conversations?${getSearchParams(params)}`,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({ type: "Chat", id: _id })),
              { type: "Chat", id: "LIST" },
            ]
          : [{ type: "Chat", id: "LIST" }],
    }),

    getOrCreateDmConversation: builder.mutation({
      query: ({ otherUserId, context }) => ({
        url: "chat/conversations/dm",
        method: "POST",
        body: { otherUserId, context },
      }),
      invalidatesTags: [{ type: "Chat", id: "LIST" }],
    }),

    createGroupConversation: builder.mutation({
      query: ({ participants, title, context }) => ({
        url: "chat/conversations",
        method: "POST",
        body: { participants, title, context },
      }),
      invalidatesTags: [{ type: "Chat", id: "LIST" }],
    }),

    /**
     * Get a specific conversation by ID
     * Used when opening a conversation from notifications
     */
    getConversationById: builder.query({
      query: (conversationId) => `chat/conversations/${conversationId}`,
      providesTags: (_result, _error, conversationId) => [
        { type: "Chat", id: conversationId },
      ],
    }),

    getConversationMessages: builder.query({
      query: ({ conversationId, ...params }) =>
        `chat/conversations/${conversationId}/messages?${getSearchParams(params)}`,
      providesTags: (_result, _error, { conversationId }) => [
        { type: "ChatMessages", id: conversationId },
      ],
    }),

    sendConversationMessage: builder.mutation({
      query: ({ conversationId, content }) => ({
        url: `chat/conversations/${conversationId}/messages`,
        method: "POST",
        body: { content },
      }),
      async onQueryStarted(
        { conversationId },
        { dispatch, getState, queryFulfilled },
      ) {
        try {
          const fulfilled = await queryFulfilled;
          patchChatCaches({
            dispatch,
            getState,
            conversationId,
            conversation: fulfilled?.data?.data?.conversation,
            message: fulfilled?.data?.data?.message,
          });
        } catch (_error) {
          // Server remains the source of truth; no optimistic patch to roll back here.
        }
      },
    }),

    markConversationRead: builder.mutation({
      query: ({ conversationId }) => ({
        url: `chat/conversations/${conversationId}/read`,
        method: "POST",
      }),
      async onQueryStarted(
        { conversationId },
        { dispatch, getState, queryFulfilled },
      ) {
        try {
          const fulfilled = await queryFulfilled;
          patchChatCaches({
            dispatch,
            getState,
            conversationId,
            conversation: fulfilled?.data?.data?.conversation,
          });
        } catch (_error) {
          // Mark-read failures should leave the existing cache unchanged.
        }
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetChatConversationsQuery,
  useGetOrCreateDmConversationMutation,
  useCreateGroupConversationMutation,
  useGetConversationByIdQuery,
  useLazyGetConversationByIdQuery,
  useGetConversationMessagesQuery,
  useLazyGetConversationMessagesQuery,
  useSendConversationMessageMutation,
  useMarkConversationReadMutation,
} = chatApi;

